// One image slot for the template editor: the asset field plus the picker it
// opens. A CONTROLLED field — it owns no server state and saves nothing on
// its own; the chosen asset's id goes up through `onChange` into the
// editor's single form, so picking an image changes the draft (and the live
// preview) and nothing else until Save. Uploading still reaches the asset
// service immediately, because that is what creating an asset means; only
// the assignment waits.
//
// No print-resolution rules: unlike vouchers' ticket artwork (which this
// family was vendored from), signage has no print constraint, so neither
// AssetField nor AssetPicker below is passed a `lowRes` prop at all.

import { Dialog, toast } from "@pej/kore/components";
import { useCallback, useState } from "react";
import {
    ASSET_KIND,
    AssetField,
    type AssetFolderSummary,
    AssetPicker,
    type AssetSummary,
    type AssetType,
} from "@/components/assets";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { isFontFile } from "@/features/assets/asset-projections";
import { useAssetById, useFolders, useFontUrl } from "@/features/assets/asset-queries";
import { useAssetPickerProps } from "@/features/assets/use-asset-picker-props";
import { useAssetUpload } from "@/features/assets/use-asset-upload";

/** The post-upload toast names where the asset landed — "Unsorted" for `null`,
 *  or a folder that's since been deleted between the upload starting and
 *  finishing (a narrow race, but one the lookup has to resolve one way). */
export function uploadDestinationName(folders: AssetFolderSummary[], folderId: string | null): string {
    return folders.find((folder) => folder.id === folderId)?.name ?? "Unsorted";
}

export interface ArtworkSlotProps {
    /** The kind of file this slot holds. Anything else is refused before it
     *  uploads: a file dialog honours `accept`, but drag and drop does not. */
    assetType: AssetType;
    /** The asset id currently in the draft; "" for none. */
    value: string;
    onChange: (assetId: string) => void;
    /** Omit on a single-field card whose own title already names the field. */
    fieldLabel?: string;
    pickerTitle: string;
    unavailableLabel: string;
}

export function ArtworkSlot({
    assetType,
    value,
    onChange,
    fieldLabel,
    pickerTitle,
    unavailableLabel,
}: ArtworkSlotProps) {
    const noun = ASSET_KIND[assetType].noun;
    const ctx = useAssetClientContext();
    const [open, setOpen] = useState(false);

    // A just-uploaded asset is displayable before the library query can find
    // it, so its summary is held here and used whenever it is what the draft
    // points at. Any other id falls through to the lookup.
    const [pendingAsset, setPendingAsset] = useState<AssetSummary | null>(null);

    const folders = useFolders(ctx);
    const pendingMatches = pendingAsset !== null && pendingAsset.id === value;
    const { asset: fetchedAsset, notFound } = useAssetById(ctx, pendingMatches || !value ? null : value);
    // An uploaded summary carries no preview URL. For a font that URL is the
    // file itself, which the manifest already knows, so the field's specimen
    // can draw the font straight after the upload.
    const pendingFontUrl = useFontUrl(ctx, pendingMatches && assetType === "FONT" ? value : null);
    const asset = pendingMatches
        ? { ...pendingAsset, thumbnailUrl: pendingAsset.thumbnailUrl ?? pendingFontUrl }
        : fetchedAsset;

    function pick(picked: AssetSummary) {
        setPendingAsset(picked);
        onChange(picked.id);
    }

    const uploadFailed = useCallback(
        () => toast.error("That file couldn't be uploaded. Try again, or choose a different file."),
        [],
    );
    const { phase, start, cancel } = useAssetUpload(
        ctx,
        (uploaded) => {
            pick({
                id: uploaded.id,
                displayName: uploaded.displayName,
                type: uploaded.type,
                state: uploaded.state,
                thumbnailUrl: null,
                width: uploaded.width,
                height: uploaded.height,
                folderId: uploaded.folderId,
            });
            toast.success(`Added to ${uploadDestinationName(folders, uploaded.folderId)}`);
        },
        uploadFailed,
    );
    const pickerProps = useAssetPickerProps(ctx, folders.length, assetType);

    function upload(files: FileList, folderId: string | null) {
        const file = files[0];
        if (!file) return;
        if (isFontFile(file) !== (assetType === "FONT")) {
            toast.error(
                assetType === "FONT"
                    ? "That's not a font file. Choose a .ttf, .otf, .woff or .woff2 file."
                    : "That's a font file. Choose an image here.",
            );
            return;
        }
        start(files, folderId);
    }

    const folderNameFor = (folderId: string | null) => folders.find((folder) => folder.id === folderId)?.name ?? null;

    if (!ctx) {
        // Supported state, not a failure: a deployment with no asset API
        // configured still edits every other part of the template.
        return (
            <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                <div className="font-medium text-foreground">{unavailableLabel}</div>
                <p className="mt-1">{noun === "font" ? "Font" : "Image"} uploads aren't available here yet.</p>
            </div>
        );
    }

    return (
        <div className="min-w-0 flex-1">
            <AssetField
                assetType={assetType}
                label={fieldLabel}
                value={asset}
                phase={phase}
                canManage
                onFiles={(files) => upload(files, null)}
                onOpenPicker={() => setOpen(true)}
                onClear={() => {
                    setPendingAsset(null);
                    onChange("");
                }}
                onCancelUpload={cancel}
                folderNameFor={folderNameFor}
            />
            {notFound && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                    This {noun} is no longer in the library. Choose another, or clear it.
                </p>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
                <Dialog.Content
                    showCloseButton={false}
                    className="w-fit max-w-[calc(100vw-2rem)] gap-0 p-0"
                    aria-label={pickerTitle}
                >
                    {pickerProps && (
                        <AssetPicker
                            {...pickerProps}
                            title={pickerTitle}
                            inUseAssetId={value || null}
                            onUpload={(files, folderId) => upload(files, folderId)}
                            onSelect={(picked) => {
                                pick(picked);
                                setOpen(false);
                            }}
                            onPreview={() => undefined}
                            onClose={() => setOpen(false)}
                        />
                    )}
                </Dialog.Content>
            </Dialog>
        </div>
    );
}
